import {createClient, mapExchange} from "@urql/core";
import {Exchange, fetchExchange, Operation} from "@urql/vue";
import {authExchange} from "@urql/exchange-auth";

import {useAuthStore} from "@/store/auth";
import {RequestQueueHelper, useRequestQueueHelper,} from "@/helper/requestQueue";
import {kapitelErrorHandler} from "@/helper/error";
import {useAppState} from "@/helper/appState";
import useOfflineHelper from "@/helper/offline";
import {Promise} from "cypress/types/cy-bluebird";
import {Kind} from "graphql/language";
import {retryExchange} from "@urql/exchange-retry";
import {FetchRetryOptions} from "@/client";

const auth = authExchange(async (utilities) => {
  const authStore = useAuthStore();

  return {
    addAuthToOperation(operation) {
      if (authStore.hasToken()) {
        operation = utilities.appendHeaders(operation, {
          Authorization: "Bearer " + authStore.getToken(),
        });
      }

      if (authStore.isImpersonating() && operation.context.useImpersonation) {
        operation = utilities.appendHeaders(operation, {
          "X-Switch-User":  authStore.getImpersonationUsername(),
        });
      }

      return operation
    },
    didAuthError(error) {
      return (
        error.graphQLErrors.some(
          (e) => e.extensions?.code === "UNAUTHORIZED",
        ) || +error.response?.status === 401
      );
    },
    async refreshAuth() {
      // logout
      useAppState().logout()      
    },
  };
});

const client = createClient({
  url: import.meta.env.VITE_GRAPHQL_ENDPOINT as string,
  exchanges: [
    mapExchange({
      onError: (e, operation) => {
        console.log(e, operation);
        if (e.networkError) {
            useOfflineHelper().checkNow()
        }
        if (operation.kind === 'mutation') {
            const message = e.graphQLErrors[0]?.originalError?.message || 'Fehler beim Speichern'
            kapitelErrorHandler(message)
        }
      },
      onOperation(operation: Operation): Promise<Operation> | Operation | void {
        const operationNode = operation.query.definitions[0]
        const operationName = operationNode.kind === Kind.OPERATION_DEFINITION ? operationNode.name?.value : undefined
        if (operationName) {
            operation.context.url = operation.context.url + '?' + operationName
        }

        return operation
      }
    }),
    auth,
    retryExchange({
        initialDelayMs: FetchRetryOptions.retryDelay,
        maxDelayMs: 2000,
        randomDelay: false,
        maxNumberAttempts: 1 + FetchRetryOptions.retries, // @defaultValue `2` - Retry once, i.e. two attempts
        // retryIf: err => err && err.networkError, @defaultValue `(error) => !!error.networkError` - retries only on network errors
    }) as Exchange,
    fetchExchange,
  ],
  fetchOptions: {
    headers: {
      "content-type": "application/json",
    },
  },
  requestPolicy: "cache-and-network", // prevent stale cache entries
});

const requestQueue: RequestQueueHelper = useRequestQueueHelper();

// const _fetch= (input: URL | RequestInfo, init?: RequestInit | undefined) => {
//     if (init?.signal) {
//         init?.signal.addEventListener('abort', () => {console.warn('kapitel: dataloader.abort not supported by urql')})
//     }
//     return fetch(input, init)
// }

export const query = async (query: any, params = {}, {useImpersonation = true, fetchOptions = undefined} : {useImpersonation?: boolean, fetchOptions?:RequestInit} = {}) => {
  return requestQueue.query(() => client.query(query, params, {useImpersonation, fetchOptions/*, fetch:_fetch*/}).toPromise());
};
export const mutation = async (query: any, params = {}, silent = false, {useImpersonation = true, fetchOptions = undefined} : {useImpersonation?: boolean, fetchOptions?:RequestInit} = {}) => {
    const mutate = () => client.mutation(query, params, {useImpersonation, fetchOptions/*, fetch:_fetch*/}).toPromise()
    // execute mutation directly without using requestQueue on silent:true thus skipping "Saving..." indicator
    return silent
        ? mutate()
        : requestQueue.mutation(mutate)
};
